import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatDateWithFormatter, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { Target, TrendingDown, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { getTargetVsActual } from '../../../services'
import type { RevenueType } from '..'

interface DensityTargetChartProps {
    filters: {
        gte?: string
        lte?: string
        employee_id?: number[]
    }
    revenueTypes?: RevenueType[]
}

const DensityTargetChart = ({
    filters,
    revenueTypes = ['renewal', 'new_sale', 'upsell'],
}: DensityTargetChartProps) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'target-vs-actual',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getTargetVsActual({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    const contributionPercent = useMemo(() => {
        const total =
            data?.employees?.reduce((acc, curr) => {
                const emp = curr as any
                let sum = 0
                if (revenueTypes.includes('renewal'))
                    sum += emp.renewal_revenue || 0
                if (revenueTypes.includes('new_sale'))
                    sum += emp.new_sale_revenue || 0
                if (revenueTypes.includes('upsell'))
                    sum += emp.upsell_revenue || 0
                return acc + sum
            }, 0) || 0
        if (!total) return 0
        const target =
            data?.employees?.reduce(
                (acc, curr) => acc + ((curr as any).target_amount || 0),
                0
            ) || 0
        if (!target) return 0
        return (total / target) * 100
    }, [data, revenueTypes])

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    const isAchieved = contributionPercent >= 100
    const displayPercent = Math.min(contributionPercent, 100)
    const remaining = 100 - displayPercent

    // Gauge data for semi-circle
    const gaugeData = [
        { name: 'achieved', value: displayPercent },
        { name: 'remaining', value: remaining },
    ]

    const periodLabel = `${formatDateWithFormatter(filters?.gte || '', 'MM/YYYY')} - ${formatDateWithFormatter(filters?.lte || '', 'MM/YYYY')}`
    const monthCount =
        dayjs(filters?.lte).diff(dayjs(filters?.gte), 'month') + 1

    return (
        <div className="flex flex-col items-center justify-center h-full py-4">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                    {t('employees.performance.target_achievement')}
                </span>
            </div>

            {/* Gauge Chart */}
            <div className="relative w-36 h-20 mb-2">
                <ResponsiveContainer width="100%" height={80}>
                    <PieChart>
                        <Pie
                            data={gaugeData}
                            cx="50%"
                            cy="100%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={45}
                            outerRadius={65}
                            dataKey="value"
                            strokeWidth={0}
                        >
                            <Cell fill={isAchieved ? '#10b981' : '#f59e0b'} />
                            <Cell fill="#e5e7eb" />
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>

            {/* Percentage display */}
            <div className="flex items-center justify-center gap-1 mb-2">
                {isAchieved ? (
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                ) : (
                    <TrendingDown className="w-5 h-5 text-amber-600" />
                )}
                <span
                    className={`text-3xl font-bold ${isAchieved ? 'text-emerald-600' : 'text-amber-600'}`}
                >
                    {formatNumeral(contributionPercent, 1)}%
                </span>
            </div>

            {/* Status badge */}
            <div
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isAchieved
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                }`}
            >
                {isAchieved
                    ? t('common.exceeded')
                    : `${t('common.remaining')} ${formatNumeral(100 - contributionPercent, 1)}%`}
            </div>

            {/* Period info */}
            <p className="mt-2 text-xs text-gray-500 text-center">
                {periodLabel} ({monthCount} {t('common.months')})
            </p>
        </div>
    )
}

export default DensityTargetChart
